import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconCash, IconPlus, IconEye, IconTrash } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'warning' },
    confirmed: { label: 'Confirmada', color: 'success' },
    cancelled: { label: 'Cancelada', color: 'default' }
};

const PayrollRuns = () => {
    const history = useHistory();
    const [appId, setAppId] = useState(null);
    const [payrollRuns, setPayrollRuns] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ reference_month: '', pay_date: '' });
    const [saving, setSaving] = useState(false);
    const mountedRef = useRef(true);

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadPayrollRuns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadPayrollRuns = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/payroll_runs/`);
            setPayrollRuns(res.data.payroll_runs || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar folhas' });
        }
    };

    const handleOpenCreate = () => {
        setForm({ reference_month: '', pay_date: '' });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.reference_month || !form.pay_date) {
            setStatus({ type: 'error', message: 'Informe o mês de referência e a data de pagamento' });
            return;
        }
        setSaving(true);
        try {
            const res = await api.post(`v1/apps/${appId}/payroll_runs/`, form);
            setDialogOpen(false);
            history.push(`/dashboard/hr/payroll/${res.data.id}`);
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao gerar folha' });
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    const handleDelete = async (payrollRun) => {
        if (!window.confirm(`Excluir a folha de ${payrollRun.reference_month}?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/payroll_runs/${payrollRun.id}/`);
            setPayrollRuns((prev) => prev.filter((p) => p.id !== payrollRun.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir folha' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconCash}
                gradient="blue"
                title="Folha de Pagamento"
                subtitle="Geração e confirmação da folha mensal"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Nova Folha
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Mês de referência</TableCell>
                                <TableCell>Data de pagamento</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payrollRuns.map((payrollRun) => {
                                const statusInfo = STATUS_LABEL[payrollRun.status] || { label: payrollRun.status, color: 'default' };
                                return (
                                    <TableRow key={payrollRun.id} hover>
                                        <TableCell>{payrollRun.reference_month}</TableCell>
                                        <TableCell>{payrollRun.pay_date}</TableCell>
                                        <TableCell>
                                            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => history.push(`/dashboard/hr/payroll/${payrollRun.id}`)}>
                                                <IconEye size={16} />
                                            </IconButton>
                                            {payrollRun.status === 'draft' && (
                                                <IconButton size="small" onClick={() => handleDelete(payrollRun)}>
                                                    <IconTrash size={16} />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {payrollRuns.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        Nenhuma folha gerada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nova Folha</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Mês de referência"
                            type="date"
                            fullWidth
                            required
                            helperText="Use o dia 1 do mês desejado"
                            InputLabelProps={{ shrink: true }}
                            value={form.reference_month}
                            onChange={(e) => setForm({ ...form, reference_month: e.target.value })}
                        />
                        <TextField
                            label="Data de pagamento"
                            type="date"
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                            value={form.pay_date}
                            onChange={(e) => setForm({ ...form, pay_date: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Gerando...' : 'Gerar Folha'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PayrollRuns;
